/**
 * Domain types for the icon-palette map marker model (spec 16 v2).
 *
 * - The 5 banners (combat/build/facility/logistic/navy) are the only taxonomy.
 * - The icon identity inside a banner is carried by `metadata.iconId`, which
 *   maps to a file in `apps/web/public/11eIcons/${iconId}.png`.
 */

/** World-space pixel coordinate (source-image coordinate system). */
export interface MapPosition {
  x: number;
  y: number;
}

/**
 * Two modes:
 * - `planner` : the original 5 banners (combat/build/facility/logistic/navy).
 * - `operation` : a separate taxonomy used by the "Operation" mode in the UI.
 *
 * Both sets of categories share the same `kind` column in DB; the `mode`
 * column on `MapObject` / `MapArrow` partitions them. The category itself
 * also implies its mode (see `getCategoryMode`).
 */
export type PlannerIconCategory = 'combat' | 'build' | 'facility' | 'logistic' | 'navy';

export type OperationIconCategory =
  | 'op_info'
  | 'op_attack'
  | 'op_defend'
  | 'op_arty'
  | 'op_build'
  | 'op_navy'
  | 'op_unit'
  | 'op_vehicle';

export type IconCategory = PlannerIconCategory | OperationIconCategory;

export type MapMode = 'planner' | 'operation';

export const PLANNER_ICON_CATEGORIES: readonly PlannerIconCategory[] = [
  'combat',
  'build',
  'facility',
  'logistic',
  'navy',
] as const;

/**
 * Order: INFO first, then ATTACK, then DEFEND, then the remaining categories
 * alphabetically (arty, build, navy, unit, vehicle).
 */
export const OPERATION_ICON_CATEGORIES: readonly OperationIconCategory[] = [
  'op_info',
  'op_attack',
  'op_defend',
  'op_arty',
  'op_build',
  'op_navy',
  'op_unit',
  'op_vehicle',
] as const;

export const ICON_CATEGORIES: readonly IconCategory[] = [
  ...PLANNER_ICON_CATEGORIES,
  ...OPERATION_ICON_CATEGORIES,
] as const;

export const MAP_MODES: readonly MapMode[] = ['planner', 'operation'] as const;

export function getCategoryMode(kind: IconCategory): MapMode {
  return (OPERATION_ICON_CATEGORIES as readonly string[]).includes(kind)
    ? 'operation'
    : 'planner';
}

/** Everything that's not position/kind is carried in metadata. */
export interface MarkerMetadata {
  /** Stable icon id, matching a PNG file (without extension) under `11eIcons/`. */
  iconId: string;
  /** Optional free-text annotation rendered in the popover. */
  note?: string | undefined;
  /** Uniform size multiplier. Default 1.0, range 0.5–1.5. */
  scale?: number | undefined;
}

/** A marker placed on the map. */
export interface MapObject {
  id: string;
  kind: IconCategory;
  position: MapPosition;
  metadata: MarkerMetadata;
  /** Partition: which map mode this object belongs to. Defaults to 'planner'. */
  mode: MapMode;
  /** ISO 8601, server-set. */
  updatedAt: string;
  /** ISO 8601 when soft-deleted, otherwise null/absent. */
  deletedAt?: string | null;
}
